// Free GCSE Revision Planner — printable, downloadable, linkable.
// Pure-frontend asset: pick exam date + subjects + study days/week, page renders
// a printable 4-week plan with built-in revision techniques (Pomodoro, retrieval,
// spaced repetition). Highly shareable, no backend, brilliant link-bait for SEO.

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Printer, Sparkle, BookOpen, ArrowRight, CheckCircle, Clock, Target,
} from '@phosphor-icons/react';
import SEO from '../components/SEO';

const SITE_URL = 'https://grade9ready.com';
const PATH = '/free-revision-planner';

const DEFAULT_SUBJECTS = ['Maths', 'English Lit', 'English Lang', 'Biology', 'Chemistry', 'Physics'];

// 8 short revision techniques — paired with each session for variety
const TECHNIQUES = [
  { name: 'Active recall',         note: 'Close the book. Write everything you remember. Check.' },
  { name: 'Past paper Q',          note: 'Pick one exam question. Time it. Self-mark.' },
  { name: 'Cornell notes',         note: 'Re-write today\'s topic in 3 columns: cue / notes / summary.' },
  { name: 'Mind map',              note: 'Centre topic. Branch out keywords + connections.' },
  { name: 'Teach-back',            note: 'Explain the topic out loud as if to a Year 7.' },
  { name: 'Flashcard drill',       note: 'Create 10 flashcards. Test until 100% recall.' },
  { name: 'Spaced re-test',        note: 'Re-test a topic from 3+ days ago. Score it.' },
  { name: 'AI-mark a 6-marker',    note: 'Write a 6-mark answer. Mark it on Grade9Ready.' },
];

function buildPlan({ examDate, subjects, daysPerWeek, hoursPerDay }) {
  if (!examDate) return null;
  const exam = new Date(examDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const msPerDay = 24 * 60 * 60 * 1000;
  const totalDays = Math.max(1, Math.ceil((exam - today) / msPerDay));
  const totalWeeks = Math.max(1, Math.ceil(totalDays / 7));
  const planWeeks = Math.min(totalWeeks, 8); // cap at 8 weeks for printability

  const weeks = [];
  for (let w = 0; w < planWeeks; w += 1) {
    const days = [];
    const start = new Date(today.getTime() + w * 7 * msPerDay);
    for (let d = 0; d < 7; d += 1) {
      const date = new Date(start.getTime() + d * msPerDay);
      const isStudy = d < daysPerWeek;
      const subject = subjects[(w * daysPerWeek + d) % Math.max(1, subjects.length)];
      const technique = TECHNIQUES[(w * 7 + d) % TECHNIQUES.length];
      days.push({
        date,
        isStudy,
        subject,
        technique,
        hours: hoursPerDay,
      });
    }
    weeks.push({ weekNum: w + 1, days });
  }
  return { totalDays, totalWeeks, planWeeks, weeks };
}

function fmtDay(d) {
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function FreeRevisionPlanner() {
  const [examDate, setExamDate] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState(2);
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [selectedSubjects, setSelectedSubjects] = useState(['Maths', 'English Lit', 'Biology', 'Chemistry', 'Physics']);

  const plan = useMemo(
    () => buildPlan({ examDate, subjects: selectedSubjects, daysPerWeek, hoursPerDay }),
    [examDate, selectedSubjects, daysPerWeek, hoursPerDay],
  );

  const toggleSubject = (s) => {
    setSelectedSubjects((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Free GCSE Revision Planner',
    description: 'Build a personalised, printable GCSE revision timetable with proven techniques (active recall, spaced repetition, Pomodoro).',
    totalTime: 'PT5M',
    step: [
      { '@type': 'HowToStep', name: 'Pick your exam date' },
      { '@type': 'HowToStep', name: 'Choose your GCSE subjects' },
      { '@type': 'HowToStep', name: 'Set your study days + hours' },
      { '@type': 'HowToStep', name: 'Print or save as PDF' },
    ],
  };

  return (
    <div className="min-h-screen bg-background" data-testid="revision-planner-page">
      <SEO
        title="Free GCSE Revision Planner — Printable Timetable + Techniques"
        description="Build a personalised, printable GCSE revision timetable in under 60 seconds. Active recall, spaced repetition, past papers — paired with every session. 100% free."
        path={PATH}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-muted-foreground print:hidden" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground font-bold">Free Revision Planner</span>
      </nav>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10 print:hidden">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-[11px] font-black uppercase tracking-wide mb-5">
          <Sparkle size={12} weight="fill" /> 100% free · no signup · printable
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] mb-4 max-w-4xl" data-testid="planner-h1">
          Free GCSE Revision Planner
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
          Pick your exam date, your subjects and your study days. We&rsquo;ll build a printable 8-week timetable that pairs every session with a proven revision technique (active recall, spaced repetition, past papers, AI-marked 6-mark practice).
        </p>
      </section>

      {/* Configurator */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 print:hidden">
        <div className="neo-card p-6 sm:p-8" data-testid="planner-configurator">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="text-xs font-black uppercase tracking-wide mb-2 flex items-center gap-1.5"><Calendar size={12} weight="fill" /> Your first exam date</label>
              <input
                type="date"
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                data-testid="planner-input-exam-date"
                className="w-full p-3 rounded-xl border-2 border-foreground/15 bg-card font-bold focus:border-primary outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wide mb-2 flex items-center gap-1.5"><Clock size={12} weight="fill" /> Hours per study day</label>
              <input
                type="number"
                min="1"
                max="6"
                value={hoursPerDay}
                onChange={(e) => setHoursPerDay(Math.max(1, Math.min(6, parseInt(e.target.value, 10) || 1)))}
                data-testid="planner-input-hours"
                className="w-full p-3 rounded-xl border-2 border-foreground/15 bg-card font-bold focus:border-primary outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wide mb-2 flex items-center gap-1.5"><Target size={12} weight="fill" /> Study days per week</label>
              <input
                type="number"
                min="1"
                max="7"
                value={daysPerWeek}
                onChange={(e) => setDaysPerWeek(Math.max(1, Math.min(7, parseInt(e.target.value, 10) || 1)))}
                data-testid="planner-input-days"
                className="w-full p-3 rounded-xl border-2 border-foreground/15 bg-card font-bold focus:border-primary outline-none"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs font-black uppercase tracking-wide mb-3 block">Pick your GCSE subjects</label>
            <div className="flex flex-wrap gap-2" data-testid="planner-subjects">
              {DEFAULT_SUBJECTS.map((s) => {
                const on = selectedSubjects.includes(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleSubject(s)}
                    data-testid={`planner-subject-${s.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-3 py-2 rounded-xl border-2 font-bold text-sm transition-all ${
                      on
                        ? 'bg-primary text-primary-foreground border-primary shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]'
                        : 'bg-card border-foreground/15 hover:border-foreground/40'
                    }`}
                  >
                    {on && <CheckCircle size={12} weight="fill" className="inline mr-1.5" />}
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {plan && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-foreground/10">
              <div className="text-sm">
                <span className="font-heading font-black text-2xl text-primary">{plan.totalDays}</span>
                <span className="text-muted-foreground ml-1">days to your first exam</span>
                <span className="mx-3 text-muted-foreground/50">·</span>
                <span className="font-heading font-black text-2xl">{plan.planWeeks}</span>
                <span className="text-muted-foreground ml-1">week plan ({selectedSubjects.length} subjects)</span>
              </div>
              <button
                onClick={handlePrint}
                data-testid="planner-print-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:translate-y-[1px] transition-transform"
              >
                <Printer size={16} weight="bold" /> Print or save as PDF
              </button>
            </div>
          )}
        </div>
      </section>

      {/* The plan */}
      {plan && selectedSubjects.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 print:px-0" data-testid="planner-output">
          <h2 className="text-2xl sm:text-3xl font-heading font-black mb-6 print:text-2xl print:mb-3">
            Your {plan.planWeeks}-week GCSE revision timetable
          </h2>
          <div className="space-y-5">
            {plan.weeks.map((week) => (
              <div key={week.weekNum} className="neo-card p-4 sm:p-5 print:break-inside-avoid print:border print:p-3">
                <div className="font-heading font-black text-lg mb-3 flex items-center justify-between">
                  <span>Week {week.weekNum}</span>
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                    {fmtDay(week.days[0].date)} → {fmtDay(week.days[6].date)}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
                  {week.days.map((d, idx) => (
                    <div
                      key={idx}
                      className={`rounded-xl p-3 border-2 text-xs ${
                        d.isStudy ? 'border-foreground/15 bg-card' : 'border-dashed border-foreground/10 bg-secondary/30 opacity-70'
                      }`}
                      data-testid={`planner-day-w${week.weekNum}-d${idx}`}
                    >
                      <div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground mb-1">
                        {fmtDay(d.date)}
                      </div>
                      {d.isStudy ? (
                        <>
                          <div className="font-heading font-bold text-sm leading-tight mb-1">{d.subject}</div>
                          <div className="text-[10px] font-bold text-primary mb-1">{d.hours} hrs</div>
                          <div className="text-[11px] leading-relaxed text-muted-foreground">
                            <strong className="block">{d.technique.name}</strong>
                            <span>{d.technique.note}</span>
                          </div>
                        </>
                      ) : (
                        <div className="text-[11px] italic text-muted-foreground">Rest day</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How to use */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 print:hidden">
        <h2 className="text-2xl font-heading font-black mb-6">How to use this planner</h2>
        <ol className="space-y-3 text-sm text-muted-foreground leading-relaxed list-decimal list-inside">
          <li><strong>Print it or save as PDF</strong> — open print dialog and pick &ldquo;Save as PDF&rdquo; on Mac/Windows.</li>
          <li><strong>Stick it somewhere visible</strong> — bedroom door, fridge, study desk.</li>
          <li><strong>Tick off each session as you go</strong> — the dopamine hit matters.</li>
          <li><strong>Pair every session with the technique listed</strong> — variety beats burnout.</li>
          <li><strong>Re-generate weekly</strong> — push your exam date and rotate techniques as you progress.</li>
        </ol>
      </section>

      {/* Soft CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 print:hidden">
        <div className="neo-card p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30">
          <h3 className="font-heading font-black text-2xl mb-2">Want the &ldquo;AI-mark a 6-marker&rdquo; sessions inside the platform?</h3>
          <p className="text-muted-foreground mb-4 max-w-2xl">
            Grade9Ready does the marking for you. Submit any answer, get instant WWW + EBI feedback, error-type analysis and a target-grade prediction.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/signup" data-testid="planner-cta-trial" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
              Create Parent Account <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/gcse-grade-boundaries-calculator" data-testid="planner-cta-calc" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40">
              <BookOpen size={16} weight="bold" /> Try the Grade Boundaries Calculator
            </Link>
          </div>
        </div>
      </section>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: #fff !important; }
        }
      `}</style>
    </div>
  );
}
